import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Sparkle, Sun, Tree, Wind, Wrench, Lightning, Cube, Drop } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { IMAGES, CATEGORIES, BRAND, BROCHURES } from "../data/content";
import { listBrochures } from "@/lib/api";

const StatItem = ({ value, label }) => (
  <div className="flex flex-col">
    <span className="font-display text-4xl md:text-5xl font-medium tracking-tighter">{value}</span>
    <span className="text-xs uppercase tracking-[0.18em] text-[#5C5A55] mt-2">{label}</span>
  </div>
);

// Fallback usato se l'API brochure non risponde (dev locale, primo deploy, ecc.)
const FALLBACK_BROCHURES = Object.values(BROCHURES).map((b, i) => ({
  id: `static-${i}`,
  title: b.title,
  description: b.desc,
  url: b.url,
}));

export default function HomePage() {
  const [brochures, setBrochures] = useState(FALLBACK_BROCHURES);

  useEffect(() => {
    let cancelled = false;
    listBrochures()
      .then((r) => {
        if (cancelled) return;
        const items = r?.items || [];
        if (items.length > 0) setBrochures(items);
      })
      .catch(() => {
        // silent: teniamo il fallback statico
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // URL dei PDF dalla libreria media (brochure caricate dall'admin); fallback statico.
  const brochureUrl = (key) =>
    brochures.find((b) => b.title === BROCHURES[key].title)?.url || BROCHURES[key].url;

  return (
    <div data-testid="home-page" className="pt-[76px]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 pt-16 lg:pt-24 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 animate-fadeup">
              <div className="overline mb-6">Sedi a Gaeta, Sabaudia e Roma</div>
              <h1 className="font-display text-[clamp(2.8rem,8.5vw,7.5rem)] leading-[0.88] tracking-tighter font-medium text-[#1A1918]">
                La luce<br />
                diventa<br />
                <span className="italic font-light text-[#EB0D0D]">architettura.</span>
              </h1>
              <p className="mt-10 max-w-xl text-lg md:text-xl text-[#5C5A55] leading-relaxed font-light">
                Infissi, vetrate panoramiche e pergole bioclimatiche su misura.
                Progettazione, fornitura e posa in opera con attenzione artigianale dal 2010.
              </p>
              <div className="flex flex-wrap gap-4 mt-10">
                <Link to="/contatti" data-testid="hero-cta-preventivatore" className="btn-primary">
                  Richiedi Preventivo
                  <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link to="/prodotti" data-testid="hero-cta-prodotti" className="btn-outline">
                  Esplora i Prodotti
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="grid grid-cols-6 gap-3 h-[480px] lg:h-[600px]">
                <div className="col-span-6 row-span-2 relative overflow-hidden rounded-sm">
                  <img src={IMAGES.hero_interior} alt="Vetrate panoramiche moderne" className="w-full h-full object-cover" />
                </div>
                <div className="col-span-3 relative overflow-hidden rounded-sm">
                  <img src={IMAGES.detail_aluminum} alt="Dettaglio alluminio" className="w-full h-full object-cover" />
                </div>
                <div className="col-span-3 relative overflow-hidden rounded-sm bg-[#1A1918] p-5 flex flex-col justify-between">
                  <Sparkle size={28} weight="duotone" className="text-[#EB0D0D]" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-[#EB0D0D] mb-2">Bonus 50%</div>
                    <div className="font-display text-sm text-[#FAF9F6]">Detrazioni fiscali fino al 50%. Pratiche incluse.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mt-24 pt-10 border-t border-[#D1CFC7]">
            <StatItem value="40+" label="Anni di esperienza" />
            <StatItem value="2.400" label="Installazioni in Lazio" />
            <StatItem value="24h" label="Preventivo gratuito" />
            <StatItem value="A+" label="Certificazione energetica" />
          </div>
        </div>
      </section>

      {/* MARQUEE PARTNERS */}
      <section className="border-y border-[#D1CFC7] bg-[#F0EFEB] overflow-hidden py-7">
        <div className="marquee-track gap-16 px-6 text-[#5C5A55] font-display text-2xl md:text-3xl tracking-tight">
          {[1, 2].map((k) => (
            <div key={k} className="flex items-center gap-16 whitespace-nowrap">
              <span>KORUS</span><span className="text-[#EB0D0D]">/</span>
              <span>GIBUS</span><span className="text-[#EB0D0D]">/</span>
              <span>BIOCLIMATIC</span><span className="text-[#EB0D0D]">/</span>
              <span>SKYWOOD</span><span className="text-[#EB0D0D]">/</span>
              <span>NEW TERMIC EVO</span><span className="text-[#EB0D0D]">/</span>
              <span>FIBERK</span><span className="text-[#EB0D0D]">/</span>
              <span>AIR SLIDE</span><span className="text-[#EB0D0D]">/</span>
              <span>LUMEN</span><span className="text-[#EB0D0D]">/</span>
            </div>
          ))}
        </div>
      </section>

      {/* KORUS — Concessionario Ufficiale */}
      <section className="bg-[#1A1918] text-[#FAF9F6] py-24 lg:py-32 relative overflow-hidden">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.3em] text-[#EB0D0D] mb-5">Concessionario ufficiale</div>
              <h2 className="font-display text-[clamp(2.4rem,6vw,5.5rem)] leading-[0.92] tracking-tighter font-medium">
                <span className="italic font-light text-[#EB0D0D]">Korus</span>.<br />
                L'eccellenza italiana in ogni profilo.
              </h2>
              <p className="mt-8 text-lg text-[#FAF9F6]/70 max-w-2xl leading-relaxed">
                Siamo concessionari ufficiali <strong className="text-[#FAF9F6]">Korus</strong>: la gamma più completa di infissi e serramenti made in Italy.
                Dalla linea <em className="text-[#EB0D0D] not-italic font-bold">Win</em> in PVC al sistema premium <em className="text-[#EB0D0D] not-italic font-bold">SkyWood EVO</em> in alluminio/legno, ogni prodotto è progettato per durare nel tempo.
              </p>
            </div>
          </div>

          {/* Hero banner Win Box Slim — full image, no crop */}
          <a
            href={brochureUrl("win_box")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="korus-banner-winbox"
            className="block mb-5 relative overflow-hidden bg-[#EB0D0D] group"
          >
            <img
              src={IMAGES.korus_winbox}
              alt="Korus — Win Box Slim, nodo centrale asimmetrico di soli 100mm. +30% di luce."
              className="w-full h-auto object-contain block transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div className="absolute top-4 right-4 bg-[#FAF9F6]/95 text-[#1A1918] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              Scarica brochure <ArrowUpRight size={14} weight="bold" />
            </div>
          </a>

          {/* KORUS product range grid */}
          <div className="grid grid-cols-12 gap-3 md:gap-5">
            <a href={brochureUrl("fiber")} target="_blank" rel="noopener noreferrer" data-testid="korus-card-fiberk" className="col-span-12 md:col-span-6 relative overflow-hidden group block">
              <img src={IMAGES.korus_fiberk} alt="FiberK Slim" className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 via-[#1A1918]/20 to-transparent" />
              <div className="absolute bottom-5 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EB0D0D] mb-1">PVC/Alluminio</div>
                <div className="font-display text-xl md:text-2xl">FiberK Slim · WinK</div>
              </div>
            </a>

            <a href={brochureUrl("alluminio")} target="_blank" rel="noopener noreferrer" data-testid="korus-card-skywood" className="col-span-12 md:col-span-6 relative overflow-hidden group block">
              <img src={IMAGES.korus_skywood} alt="SkyWood EVO" className="w-full h-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 via-[#1A1918]/20 to-transparent" />
              <div className="absolute bottom-5 left-6 right-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EB0D0D] mb-1">Alluminio/Legno · Premium</div>
                <div className="font-display text-xl md:text-2xl">SkyWood EVO Original</div>
              </div>
            </a>

            <a href={brochureUrl("win_box")} target="_blank" rel="noopener noreferrer" data-testid="korus-card-lumen" className="col-span-6 md:col-span-3 relative overflow-hidden group block">
              <img src={IMAGES.korus_lumen} alt="Lumen / LumenK" className="w-full h-full object-cover aspect-square transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EB0D0D]">PVC · PVC/All.</div>
                <div className="font-display text-lg">Lumen · LumenK</div>
              </div>
            </a>

            <a href={brochureUrl("alluminio")} target="_blank" rel="noopener noreferrer" data-testid="korus-card-newtermic" className="col-span-6 md:col-span-3 relative overflow-hidden group block">
              <img src={IMAGES.korus_newtermic} alt="New Termic EVO" className="w-full h-full object-cover aspect-square transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EB0D0D]">Alluminio</div>
                <div className="font-display text-lg">New Termic EVO</div>
              </div>
            </a>

            <a href={brochureUrl("alluminio")} target="_blank" rel="noopener noreferrer" data-testid="korus-card-airslide" className="col-span-6 md:col-span-3 relative overflow-hidden group block">
              <img src={IMAGES.korus_airslide} alt="Air Slide" className="w-full h-full object-cover aspect-square transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EB0D0D]">Scorrevole</div>
                <div className="font-display text-lg">Air Slide · Wood</div>
              </div>
            </a>

            <a href={brochureUrl("alluminio")} target="_blank" rel="noopener noreferrer" data-testid="korus-card-aluwood" className="col-span-6 md:col-span-3 relative overflow-hidden group block">
              <img src={IMAGES.korus_aluwood} alt="SkyWood EVO Linear" className="w-full h-full object-cover aspect-square transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EB0D0D]">EVO Linear</div>
                <div className="font-display text-lg">SkyWood Linear</div>
              </div>
            </a>
          </div>

          <div className="mt-14 flex flex-wrap gap-4 items-center justify-between">
            <p className="text-sm text-[#FAF9F6]/50 max-w-2xl">
              Tutta la gamma Korus è disponibile nel nostro showroom di Gaeta. Vieni a toccare con mano materiali, finiture e profili in scala reale.
            </p>
            <Link to="/prodotti" data-testid="korus-cta" className="inline-flex items-center gap-3 bg-[#EB0D0D] hover:bg-[#C00A0A] text-[#FAF9F6] px-7 py-4 text-sm uppercase tracking-[0.15em] font-bold rounded-sm transition-all">
              Esplora la gamma <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>

          {/* Brochure downloads */}
          {brochures.length > 0 && (
            <div className="mt-16 pt-12 border-t border-[#FAF9F6]/15">
              <div className="text-xs uppercase tracking-[0.25em] text-[#EB0D0D] mb-6">Brochure ufficiali Korus</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {brochures.map((b) => (
                  <a
                    key={b.id}
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`brochure-${b.id}`}
                    className="group flex items-start justify-between gap-4 p-6 border border-[#FAF9F6]/15 hover:border-[#EB0D0D] hover:bg-[#FAF9F6]/[0.03] transition-all"
                  >
                    <div className="flex-1">
                      <div className="font-display text-lg md:text-xl tracking-tight mb-1">{b.title}</div>
                      {b.description && (
                        <div className="text-xs text-[#FAF9F6]/60 leading-relaxed">{b.description}</div>
                      )}
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#EB0D0D] mt-3 font-bold">PDF · Scarica</div>
                    </div>
                    <ArrowUpRight size={20} weight="bold" className="text-[#FAF9F6]/40 group-hover:text-[#EB0D0D] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Video showcase */}
          <div className="mt-16 pt-12 border-t border-[#FAF9F6]/15">
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-10 max-w-4xl">
              Infissi nuovi senza sconto in fattura? <em className="italic font-light text-[#EB0D0D]">Con Korus ora si può!</em>
            </h3>
            <div className="relative w-full aspect-video overflow-hidden bg-black" data-testid="homepage-video">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/GHShlKosbfY"
                title="Koreko Direct — Video showcase"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES — Editorial bento */}
      <section className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-36">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {CATEGORIES.map((cat, i) => (
            <article
              key={cat.slug}
              data-testid={`category-card-${cat.slug}`}
              className={`group relative overflow-hidden bg-[#F0EFEB] ${
                i % 4 === 0 ? "md:col-span-7" :
                i % 4 === 1 ? "md:col-span-5" :
                i % 4 === 2 ? "md:col-span-5" : "md:col-span-7"
              }`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-7 md:p-10">
                <div className="overline mb-3">{cat.overline}</div>
                <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-4">{cat.title}</h3>
                <p className="text-[#5C5A55] leading-relaxed mb-6">{cat.desc}</p>
                <div className="flex flex-wrap gap-2 mb-7">
                  {cat.items.map((it) => (
                    <span key={it} className="text-xs uppercase tracking-wider border border-[#D1CFC7] px-3 py-1.5 text-[#5C5A55]">
                      {it}
                    </span>
                  ))}
                </div>
                <Link to="/prodotti" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] text-[#1A1918] hover:text-[#EB0D0D] transition-colors group/link">
                  Scopri
                  <ArrowRight size={16} weight="bold" className="transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* PREVENTIVATORE TEASER */}
      <section className="bg-[#1A1918] text-[#FAF9F6] py-28 lg:py-36 relative overflow-hidden grain">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-[#EB0D0D] mb-6">Novità · Calcola online</div>
            <h2 className="font-display text-[clamp(2.4rem,6vw,5.5rem)] leading-[0.93] tracking-tighter font-medium">
              Un preventivo,<br />in <em className="italic font-light text-[#EB0D0D]">tre minuti</em>.
            </h2>
            <p className="mt-8 text-lg text-[#FAF9F6]/70 max-w-xl leading-relaxed">
              Scegli tipologia, materiale e dimensioni: ricevi subito una stima di costo trasparente e un preventivo ufficiale entro 24 ore.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12 mb-12">
              {[
                { i: <Cube size={26} weight="duotone" />, t: "Configura", d: "Tipologia, materiale, misure." },
                { i: <Lightning size={26} weight="duotone" />, t: "Stima istantanea", d: "Range di prezzo trasparente." },
                { i: <Wrench size={26} weight="duotone" />, t: "Sopralluogo", d: "Tecnico dedicato, gratis." },
              ].map((s, idx) => (
                <div key={idx} className="border-l-2 border-[#EB0D0D] pl-4 py-2">
                  <div className="text-[#EB0D0D] mb-3">{s.i}</div>
                  <div className="font-display text-xl mb-1">{s.t}</div>
                  <div className="text-sm text-[#FAF9F6]/60">{s.d}</div>
                </div>
              ))}
            </div>

            <Link to="/contatti" data-testid="teaser-cta-preventivatore" className="inline-flex items-center gap-3 bg-[#EB0D0D] hover:bg-[#C00A0A] text-[#FAF9F6] px-7 py-4 text-sm uppercase tracking-[0.15em] font-bold rounded-sm transition-all">
              Richiedi Preventivo
              <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative h-[480px] overflow-hidden rounded-sm">
              <img src={IMAGES.interior_minimalist} alt="Interno minimalista" className="w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-[#FAF9F6]/95 backdrop-blur-md p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#EB0D0D] mb-2">Esempio stima</div>
                <div className="flex justify-between items-baseline">
                  <div className="font-display text-[#1A1918] text-base">2 ante · Alluminio · 120×140cm</div>
                  <div className="font-display text-2xl font-bold text-[#1A1918]">€ 980<span className="text-sm text-[#5C5A55] font-normal">–1.250</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE BLOCK */}
      <section className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-2 hidden lg:block">
            <div className="w-12 h-[2px] bg-[#EB0D0D] mt-6" />
          </div>
          <div className="lg:col-span-10">
            <blockquote className="font-display text-[clamp(1.8rem,4vw,3.6rem)] leading-tight tracking-tight text-[#1A1918] font-medium">
              "La tua casa è l'espressione della tua vita.
              <span className="italic font-light text-[#5C5A55]"> Cresce con te, respira con te."</span>
            </blockquote>
            <div className="mt-8 text-xs uppercase tracking-[0.25em] text-[#5C5A55]">
              — Mark Fisher
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE / PROCESS */}
      <section className="bg-[#F0EFEB] py-28 lg:py-36">
        <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="overline mb-4">Il processo</div>
          <h2 className="font-display text-[clamp(2.2rem,5vw,4.5rem)] leading-[0.95] tracking-tighter font-medium mb-16 max-w-3xl">
            Dal primo contatto<br />alla posa: <em className="italic font-light text-[#EB0D0D]">cinque passi</em>.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { n: "01", t: "Contatto", d: "Chiamata, form o WhatsApp. Risposta entro 24h." },
              { n: "02", t: "Sopralluogo", d: "Tecnico in loco. Misure, esigenze, consigli." },
              { n: "03", t: "Preventivo", d: "Offerta dettagliata e trasparente, senza impegno." },
              { n: "04", t: "Produzione", d: "Materiali certificati, lavorati su misura." },
              { n: "05", t: "Posa", d: "Installazione professionale e pulizia finale." },
            ].map((step) => (
              <div key={step.n} className="border-t-2 border-[#1A1918] pt-5">
                <div className="font-display text-sm font-bold text-[#EB0D0D] mb-3">{step.n}</div>
                <div className="font-display text-xl mb-2">{step.t}</div>
                <div className="text-sm text-[#5C5A55] leading-relaxed">{step.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMO BANNER */}
      <section className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-24">
        <div className="bg-[#EB0D0D] text-[#FAF9F6] p-10 md:p-16 lg:p-20 relative overflow-hidden">
          <div className="absolute top-0 right-0 font-display text-[20rem] leading-none opacity-10 -mt-8 select-none">50%</div>
          <div className="relative max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] mb-4 opacity-80">Bonus fiscali · 2025</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95] font-medium mb-6">
              Sconto fino al<br />50% sull'infisso.
            </h2>
            <p className="text-lg opacity-90 mb-8 leading-relaxed">
              Gestiamo tutte le pratiche per detrazioni fiscali, Ecobonus e Conto Termico. Tu pensi al design, noi alla burocrazia.
            </p>
            <Link to="/detrazioni-fiscali" data-testid="promo-cta" className="inline-flex items-center gap-3 bg-[#FAF9F6] text-[#1A1918] px-7 py-4 text-sm uppercase tracking-[0.15em] font-bold rounded-sm hover:bg-[#1A1918] hover:text-[#FAF9F6] transition-all">
              Scopri come funziona
              <ArrowUpRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
